import { useState } from 'react';
import { MakeCopy } from 'ui/icons/action/MakeCopy';
import { Success } from 'ui/icons/status/Success';
import { colors } from 'ui/theme/colors';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { updateClipboard } from 'utils/clipboard';
import { Body, Code, SvgContainer } from './ClickToCopy.styled';
import type { ClickToCopyCodeProps } from './types';

const DEFAULT_TOOLTIP_CONTENT = {
  default: 'Copy',
  error: 'Could not copy',
  success: 'Copied!',
};

/**
 * ClickToCopyCode
 * Allows the user to copy a string that wrapped in a code block to the clipboard
 */
export const ClickToCopyCode = ({
  'data-test': dataTest = 'click-to-copy-code',
  content,
  isCopyable = true,
  onCopyCallback,
  prependContent,
  shouldFillHeight = false,
  tooltipContent = DEFAULT_TOOLTIP_CONTENT,
  children,
  ...props
}: ClickToCopyCodeProps) => {
  const [copyTooltipText, setCopyTooltipText] = useState(tooltipContent.default);
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);

  const handleCopyClick = () => {
    if (!isCopyable) return;

    updateClipboard(content)
      .then(() => setCopyTooltipText(tooltipContent.success))
      .catch(() => setCopyTooltipText(tooltipContent.error))
      .finally(() => onCopyCallback?.());
  };

  const showTooltip = () => {
    setCopyTooltipOpen(true);
  };

  const hideTooltip = () => {
    setCopyTooltipOpen(false);
    setCopyTooltipText(tooltipContent.default);
  };

  return (
    <Tooltip tooltipContent={copyTooltipText} open={copyTooltipOpen}>
      <Body
        {...props}
        data-test={dataTest}
        data-fillheight={shouldFillHeight}
        onClick={handleCopyClick}
        // Toggle tooltip with keyboard
        onBlur={hideTooltip}
        onFocus={showTooltip}
        // Toggle tooltip with mouse
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {prependContent && <Code data-secondarystyle>{prependContent}</Code>}
        <Code data-test={`${dataTest}--code-block`}>{children ?? content}</Code>
        {isCopyable && (
          <SvgContainer data-test={`${dataTest}--svg-container`}>
            {copyTooltipText === tooltipContent.default ||
            copyTooltipText === tooltipContent.error ? (
              <MakeCopy width={20} height={20} color={colors.secondaryText} />
            ) : (
              <Success width={20} height={20} />
            )}
          </SvgContainer>
        )}
      </Body>
    </Tooltip>
  );
};
